import { useCallback, useRef, useState } from 'react';
import { Checkbox, Dialog, Dialogs, PropertyGrid, TextInput } from '@leverege/ui-elements';
import { Config, Context } from '@leverege/plugin';
import { getKonvaLayers } from '@leverege/ui-image-overlay';
import { LoadingView } from '@leverege/molten/views';
import P from '@leverege/promise';
import { Stage, Layer, Image as KonvaImage } from 'konva';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import { GlobalState, UI } from '@leverege/ui-redux';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const DEFAULT_LAYER_SETTINGS_PATH = 'rsrc/overlays';
export default {
  id: 'rsrc.bulkExportImages',
  name: 'Download Resources',
  matches: {
    client: ['GroupScreen', 'rsrc.gallery.RsrcThumbnailRenderer', 'rsrc.gallery.RsrcSelectedDialog'],
    use: ['contextMenu', 'mobileActionBar'],
    objectType: 'rsrc'
  },
  layout: {
    sort: 'item.bulkExport',
    sectionName: 'Resource Actions'
  },
  handles: context => {
    const {
      clientProps: {
        items
      }
    } = context;
    const targets = Context.getTargetsOfType(context, 'rsrc');
    return items?.items?.length > 0 || targets?.length > 0;
  },
  appearance: ({
    context
  }) => {
    const targets = Context.getTargetsOfType(context, 'rsrc');
    return {
      name: targets?.length > 0 ? `Download Resource${targets.length === 1 ? '' : 's'} (${targets.length})` : 'Download Resources (All on Page)',
      icon: 'fa-fw fa-solid fa-download'
    };
  },
  perform: ({
    context
  }) => {
    const {
      clientProps: {
        items,
        relationship,
        delegate
      }
    } = context;
    // show a dialog that just updates every resource in the list
    const targets = Context.getTargetsOfType(context, 'rsrc');
    const getItems = targets?.length > 0 ? () => ({
      items: targets,
      total: targets.length,
      offset: 0,
      limit: 200
    }) : async () => {
      return {
        ...items,
        items: items?.items?.filter(item => item != null).map(item => {
          return delegate.toObjectRef(item);
        })
      };
    };
    Dialogs.show({
      component: BulkExportDialog,
      props: {
        getItems,
        onOkay: exportImages,
        path: relationship.path,
        relationship,
        context
      }
    });
  }
};
function BulkExportDialog(props) {
  const {
    show,
    onClose,
    onOkay: pOnOkay,
    getItems,
    path,
    context
  } = props;
  const [useCanvas, setUseCanvas] = useState(true);
  const [filename, setFilename] = useState('exports.zip');
  const containerRef = useRef();
  const onOkay = useCallback(async () => {
    let res = getItems();
    if (res.then) {
      res = await res;
    }
    const {
      items
    } = res;
    const close = LoadingView.showDialog('Exporting Images...');
    await pOnOkay?.(items, {
      useCanvas,
      filename,
      done: onClose,
      path,
      context
    });
    close();
  }, [pOnOkay, getItems, onClose, useCanvas, filename, path]);
  const onKeyDown = useCallback(e => {
    e.stopPropagation();
  }, []);
  return /*#__PURE__*/_jsxs(Dialog.Question, {
    show: show,
    onClose: onClose,
    onCancel: onClose,
    onOkay: onOkay,
    title: "Bulk Export",
    children: [/*#__PURE__*/_jsxs(PropertyGrid, {
      variant: "imageExport",
      onKeyDown: onKeyDown,
      children: [/*#__PURE__*/_jsx(PropertyGrid.Item, {
        label: /*#__PURE__*/_jsx(Checkbox, {
          label: "Show Overlays?",
          value: useCanvas,
          onChange: ({
            value
          }) => {
            setUseCanvas(value);
          }
        }),
        fillCell: true
      }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
        label: "Filename",
        children: /*#__PURE__*/_jsx(TextInput, {
          value: filename,
          onChange: ({
            value
          }) => {
            setFilename(value);
          }
        })
      })]
    }), /*#__PURE__*/_jsx("div", {
      ref: containerRef,
      id: "container"
    })]
  });
}
async function exportImages(items, {
  useCanvas,
  filename,
  done,
  path,
  context
}) {
  const zip = new JSZip();
  await P.mapLimit(items, async item => {
    if (!item.data.type.startsWith('image/')) {
      return;
    }
    const container = document.createElement('div');
    const stage = new Stage({
      container
    });
    const image = await loadImage(item.data.data.url);
    container.style = `width: ${image.naturalWidth}px; height: ${image.naturalHeight}px;`;
    stage.setAttrs({
      width: image.naturalWidth,
      height: image.naturalHeight
    });
    const img = new KonvaImage({
      image
    });
    const layer = new Layer();
    layer.add(img);
    stage.add(layer);
    if (useCanvas) {
      const getLayersFromRsrc = Config.get('OverlayImageRenderers', `${path}/layerGetter`, Config.get('OverlayImageRenderers', 'layerGetter', () => ({
        layers: []
      })));
      const layerSettingsPath = Config.get('OverlayImageRenderers', 'layerSettingsPath', DEFAULT_LAYER_SETTINGS_PATH);
      const getLayerSettings = state => UI.get(state, layerSettingsPath);
      const settings = getLayerSettings(GlobalState.get());
      const layers = getLayersFromRsrc({
        item,
        image,
        settings
      });
      const {
        layers: kLayers
      } = await getKonvaLayers({
        ...layers,
        context: {
          ...context,
          settings
        }
      });
      if (kLayers.length > 0) {
        const flat = kLayers.flat(Infinity);
        layer.add(...flat);
      }
    }
    const out = await stage.toBlob();
    zip.file(`${item.data.path.replaceAll('/', '_')}.png`, out);
  }, {
    limit: 5
  });
  zip.generateAsync({
    type: 'blob'
  }).then(blob => {
    saveAs(blob, filename);
    done();
  });
}
async function loadImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve(img);
    };
    img.onerror = err => {
      reject(err);
    };
    img.crossOrigin = 'anonymous';
    img.src = url;
  });
}